import React from "react";
import {
  Button,
  InputLabel,
  Grid,
  CircularProgress,
  TextField,
  FormControl,
  Select,
  MenuItem,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { Delete, Lock, Edit, Save, Add } from "@mui/icons-material";
import Autocomplete from "@mui/material/Autocomplete";

import BasicTable2 from "../tools/BasicTable2";
import * as ETVConstants from "../ETVConstants";
import MessageDialog from "../tools/MessageDialog";

class FaceTerminalMgmt extends React.Component {
  state = {
    // data
    locations: [],
    terminals: [],
    devices: [],

    // loading flags
    loadingLocations: false,
    terminalsLoading: false,
    devicesLoading: false,

    // loaded flags
    locationsLoaded: false,
    terminalsLoaded: false,
    devicesLoaded: false,

    // selection
    selected_terminal_id: undefined,
    selected_location_id: undefined,

    // dialog/window + action
    window: "",
    action: undefined, // "add_terminal" | "edit_terminal"
    terminal_id: "",

    // info dialog
    info_type: "",
    info_title: undefined,
    info_message: undefined,
    spinning: false,

    // dialog form
    form: {
      name: "",
      deviceID: "",
      terminal_type: "",
      ip_address: "",
    },
  };

  componentDidMount() {
    this.loadLocations();
  }

  componentDidUpdate(prevProps, prevState) {
    // (Re)load terminals/devices when a location is set and we mark them stale
    if (
      this.state.selected_location_id &&
      (!this.state.terminalsLoaded && !this.state.terminalsLoading)
    ) {
      this.loadTerminals();
    }
    if (
      this.state.selected_location_id &&
      (!this.state.devicesLoaded && !this.state.devicesLoading)
    ) {
      this.loadDevices();
    }
  }

  // -----------------------------
  // Fetchers
  // -----------------------------
  loadLocations = () => {
    this.setState({ loadingLocations: true });
    const url =
      ETVConstants.getServerBase() +
      "/floor_access?action=get_locations&user_id=" +
      this.props.user_id;

    fetch(url)
      .then((res) => res.json())
      .then(
        (result) => {
          let locID = undefined;
          if (Array.isArray(result.list) && result.list.length === 1) {
            locID = result.list[0].location_id;
          }
          this.setState({
            locations: result.list || [],
            locationsLoaded: true,
            loadingLocations: false,
            selected_location_id: locID,
            // mark downstream lists as stale so componentDidUpdate will load them
            terminalsLoaded: false,
            devicesLoaded: false,
          });
        },
        (error) => {
          this.setState({
            locations: [],
            locationsLoaded: false,
            loadingLocations: false,
          });
          this.showErrorMessage("Error", "Failed to load locations");
          console.error(error);
        }
      );
  };

  loadTerminals = () => {
    if (!this.state.selected_location_id) return;
    this.setState({ terminalsLoading: true });
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    const url =
      ETVConstants.getServerBase() +
      "/face_terminals?action=list_terminals_by_location&location_id=" +
      this.state.selected_location_id +
      "&user_id=" +
      reqUserID;

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          this.setState({
            terminals: result.list || [],
            terminalsLoaded: true,
            terminalsLoading: false,
          });
        },
        (error) => {
          this.setState({
            terminals: [],
            terminalsLoaded: false,
            terminalsLoading: false,
          });
          this.showErrorMessage("Error", "Failed to load terminals");
          console.error(error);
        }
      );
  };

  loadDevices = () => {
    if (!this.state.selected_location_id) return;
    this.setState({ devicesLoading: true });
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    const url =
      ETVConstants.getServerBase() +
      "/face_terminals?action=list_devices_by_location&location_id=" +
      this.state.selected_location_id +
      "&user_id=" +
      reqUserID;

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          this.setState({
            devices: result.list || [],
            devicesLoaded: true,
            devicesLoading: false,
          });
        },
        (error) => {
          this.setState({
            devices: [],
            devicesLoaded: false,
            devicesLoading: false,
          });
          this.showErrorMessage("Error", "Failed to load devices");
          console.error(error);
        }
      );
  };

  // -----------------------------
  // Helpers
  // -----------------------------
  getTerminal = (terminalID) => {
    return this.state.terminals.find((t) => t.terminal_id === terminalID) || null;
  };

  showInfoMessage = (title, msg, isSpinning = false) => {
    this.setState({
      window: "message_div",
      info_type: "info",
      info_title: title,
      info_message: msg,
      spinning: isSpinning,
    });
  };

  showSuccessMessage = (title, msg) => {
    this.setState({
      window: "message_div",
      info_type: "success",
      info_title: title,
      info_message: msg,
      spinning: false,
    });
  };

  showErrorMessage = (title, msg) => {
    this.setState({
      window: "message_div",
      info_type: "error",
      info_title: title,
      info_message: msg,
      spinning: false,
    });
  };

  closeInfoMessage = () => {
    this.setState({
      window: "",
      info_type: "",
      info_title: "",
      info_message: undefined,
      spinning: false,
    });
  };

  // -----------------------------
  // Dialog field handling
  // -----------------------------
  handleFieldChange = (key) => (e) => {
    const value = e?.target?.value ?? "";
    this.setState((prev) => ({ form: { ...prev.form, [key]: value } }));
  };

  // -----------------------------
  // CRUD: Add / Edit / Delete
  // -----------------------------
  addTerminal = () => {
    this.setState({
      window: "add_terminal_div",
      action: "add_terminal",
      terminal_id: "",
      form: { name: "", deviceID: "", terminal_type: "", ip_address: "" },
    });
  };

  editTerminal = (terminalID) => {
    const t = this.getTerminal(terminalID);
    if (!t) return;
    this.setState({
      window: "add_terminal_div",
      action: "edit_terminal",
      terminal_id: t.terminal_id,
      form: {
        name: t.name || "",
        deviceID: t.device_id || "",
        terminal_type: t.terminal_type || "",
        ip_address: t.ip_addr || t.ip_address || "",
      },
    });
  };

  deleteTerminal = (terminalID) => {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    let url = ETVConstants.getServerBase() + "/face_terminals?action=delete_terminal";
    url += "&terminal_id=" + encodeURIComponent(terminalID);
    console.log("delete url="+url);
    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(() => {
        this.setState({ window: "", terminalsLoaded: false });
      })
      .catch((error) => {
        this.showErrorMessage("Error", "Failed to delete terminal");
        console.error(error);
      });
  };

  submitAddTerminal = () => {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    const { name, deviceID, terminal_type, ip_address } = this.state.form;

    let url = ETVConstants.getServerBase() + "/face_terminals?action=add_terminal";
    url += "&name=" + encodeURIComponent(name || "");
    url += "&device_id=" + encodeURIComponent(deviceID || "");
    url += "&terminal_type=" + encodeURIComponent(terminal_type || "");
    url += "&ip_addr=" + encodeURIComponent(ip_address || "");
    url += "&user_id=" + encodeURIComponent(reqUserID || "");
    url += "&location_id=" + encodeURIComponent(this.state.selected_location_id || "");

    console.log("add terminal url="+url);
    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(() => {
          this.setState({ window: "", terminalsLoaded: false });
      })
      .catch((error) => {
        this.showErrorMessage("Error", "Failed to save terminal");
        console.error(error);
      });
  };

  deviceName = (id) => {
    const d = (this.state.devices || []).find(x => x.device_id === id);
    return d?.device_name || id || "";
  };

  updateTerminal = () => {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    const { terminal_id, form } = this.state;
    let url = ETVConstants.getServerBase() + "/face_terminals?action=update_terminal";
    url += "&terminal_id=" + encodeURIComponent(terminal_id || "");
    url += "&name=" + encodeURIComponent(form.name || "");
    url += "&device_id=" + encodeURIComponent(form.deviceID || "");
    url += "&terminal_type=" + encodeURIComponent(form.terminal_type || "");
    url += "&ip_addr=" + encodeURIComponent(form.ip_address || "");
    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(() => {
        this.setState({ window: "", terminalsLoaded: false });
      })
      .catch((error) => {
        this.showErrorMessage("Error", "Failed to update terminal");
        console.error(error);
      });
  };

  setPassword( terminalID) {
    this.setState({window:'set_pw_div',selected_terminal_id:terminalID});
  }

  setTerminalPassword = () => {
    const accessToken = localStorage.getItem("etv.access_token");
    const reqUserID = localStorage.getItem("etv.user_id");
    let url = ETVConstants.getServerBase() + "/face_terminals?action=set_password";
    url += "&terminal_id=" + encodeURIComponent(this.state.selected_terminal_id);
    url += "&pw=" + encodeURIComponent(this.state.pw);
    console.log("setting terminal pw="+url);
    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(() => {
        this.setState({ window: ""});
      })
      .catch((error) => {
        this.showErrorMessage("Error", "Failed to set terminal password");
        console.error(error);
      });
  };


  render() {
    const columnsDataTerminal = [
      { title: ETVConstants.trans("name"), field: "name" },
      { title: ETVConstants.trans("terminal_type"), field: "terminal_type" },
      { title: ETVConstants.trans("ip_address"), field: "ip_addr" },
      { title: ETVConstants.trans("device"), field: "device_id", render: (row) => this.deviceName(row.device_id),  },
      { title: ETVConstants.trans("set_password"), field: "",render: (row) => (<IconButton size="small" onClick={() => this.setPassword(row.terminal_id)}><Lock /></IconButton>),},
      {
        title: ETVConstants.trans("edit"),
        field: "",
        render: (row) => (
          <IconButton
            size="small"
            color="primary"
            onClick={() => this.editTerminal(row.terminal_id)}
          >
            <Edit />
          </IconButton>
        ),
      },
      {
        title: ETVConstants.trans("delete"),
        field: "",
        render: (row) => (
          <IconButton
            size="small"
            color="error"
            onClick={() => this.deleteTerminal(row.terminal_id)}
          >
            <Delete />
          </IconButton>
        ),
      },
    ];

    const disableSave =
      !this.state.form.name ||
      !this.state.form.deviceID ||
      !this.state.form.terminal_type;

    return (
      <div>
        {/* Location selector */}
        <div style={{ padding: 20, width: 400 }}>
          <Autocomplete
            options={this.state.locations || []}
            getOptionLabel={(option) =>
              option?.name ? String(option.name) : ""
            }
            value={
              this.state.locations.find(
                (loc) => loc.location_id === this.state.selected_location_id
              ) || null
            }
            onChange={(event, newValue) => {
              this.setState({
                selected_location_id: newValue ? newValue.location_id : "",
                terminalsLoaded: false,
                devicesLoaded: false,
              });
            }}
            isOptionEqualToValue={(option, value) =>
              !!option &&
              !!value &&
              option.location_id === value.location_id
            }
            loading={this.state.loadingLocations}
            renderInput={(params) => (
              <TextField
                {...params}
                label={ETVConstants.trans("select_location")}
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {this.state.loadingLocations ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </div>

        {this.state.terminalsLoading && <LinearProgress />}

        {this.state.selected_location_id ? (
          <Card elevation={2}>
            <CardHeader
              title={ETVConstants.trans("terminals")}
              action={
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={this.addTerminal}
                  sx={{ mt: 1 }}
                >
                  {ETVConstants.trans("add_terminal")}
                </Button>
              }
              sx={{ pb: 1 }}
            />
            <CardContent sx={{ p: 0 }}>
              <BasicTable2 data={this.state.terminals} columns={columnsDataTerminal} title="" pageSize={20} allow_search />
            </CardContent>
          </Card>
        ) : (
          <span />
        )}

        {/* Add/Edit Dialog */}
        <Dialog
          open={this.state.window === "add_terminal_div"}
          maxWidth="sm"
          fullWidth
          onClose={() => this.setState({ window: "" })}
          aria-labelledby="add-card-dialog-title"
        >
          <DialogTitle id="add-card-dialog-title">
            {this.state.action === "add_terminal"
              ? ETVConstants.trans("add_terminal")
              : ETVConstants.trans("edit_terminal")}
          </DialogTitle>

          <DialogContent dividers>
            <Grid container spacing={2}>
              {/* Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={ETVConstants.trans("name")}
                  value={this.state.form.name}
                  onChange={this.handleFieldChange("name")}
                  variant="outlined"
                />
              </Grid>

              {/* Device ID */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="device-select-label">
                    {ETVConstants.trans("deviceID")}
                  </InputLabel>
                  <Select
                    labelId="device-select-label"
                    label={ETVConstants.trans("deviceID")}
                    value={this.state.form.deviceID}
                    onChange={this.handleFieldChange("deviceID")}
                  >
                    {(this.state.devices || []).map((d) => (
                      <MenuItem key={d.device_id} value={d.device_id}>
                        {d.device_name || d.device_id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Terminal Type */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="terminal-type-label">
                    {ETVConstants.trans("terminal_type")}
                  </InputLabel>
                  <Select
                    labelId="terminal-type-label"
                    label={ETVConstants.trans("terminal_type")}
                    value={this.state.form.terminal_type}
                    onChange={this.handleFieldChange("terminal_type")}
                  >
                    {["shenzhen_wellcam", "shenzhen_yistar", "hikvision"].map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* IP Address */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={ETVConstants.trans("ip_address")}
                  placeholder="192.168.1.100"
                  value={this.state.form.ip_address}
                  onChange={this.handleFieldChange("ip_address")}
                  variant="outlined"
                  inputProps={{ inputMode: "numeric" }}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => this.setState({ window: "" })}
              color="primary"
            >
              {ETVConstants.trans("close")}
            </Button>
            <Button
              onClick={
                this.state.action === "add_terminal"
                  ? this.submitAddTerminal
                  : this.updateTerminal
              }
              color="primary"
              startIcon={<Save />}
              variant="contained"
              disabled={disableSave}
            >
              {this.state.action === "add_terminal"
                ? ETVConstants.trans("save")
                : ETVConstants.trans("update")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={this.state.window === "set_pw_div"} /* ... */>
          <DialogContent dividers>
            <TextField
              fullWidth
              label={ETVConstants.trans("password")}
              type="password"
              value={this.state.pw}                      // <-- use pw, not form.name
              onChange={(e) => this.setState({ pw: e.target.value })}
              variant="outlined"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ window: "" })} color="primary">
              {ETVConstants.trans("close")}
            </Button>
            <Button
              onClick={this.setTerminalPassword}         // <-- call the right saver
              color="primary"
              startIcon={<Save />}
              variant="contained"
              disabled={!this.state.pw}                  // <-- simple guard
            >
              {ETVConstants.trans("save")}
            </Button>
          </DialogActions>
        </Dialog>


        {/* Info / Error dialog */}
        <MessageDialog
          spinning={this.state.spinning}
          type={this.state.info_type}
          open={this.state.window === "message_div"}
          onClose={this.closeInfoMessage}
          title={this.state.info_title}
          message={this.state.info_message}
        />
      </div>
    );
  }
}

export default FaceTerminalMgmt;
